import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError } from "@/api/fetch";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { isStaff } from "@/features/auth/utils/staff";
import { CopyButton, CSS as PAGE_CSS } from "@/features/claim-hunter/pages/ClaimHunterPage";
import { searchExperts } from "../api/experts";
import { ExpertCard } from "../components/ExpertCard";
import { ExpertsFaq } from "../components/ExpertsFaq";
import type { ExpertsSearchResponse, RankBy } from "../types";
import {
  CITATIONS_SOURCE,
  changedOptions,
  citedByText,
  isEmerging,
  RANK_BY_LABELS,
} from "../utils/factors";
import { expertsMarkdown } from "../utils/markdown";
import { paperUrl } from "../utils/links";

/** Search results read per query; the API takes 20-1000. Over 250 is four searches by year. */
const PAPER_STEPS = [150, 500, 1000];
const DEFAULT_PAPERS = 1000;
const PEOPLE = 25;

const EXPERTS_CSS = `
.xp-list { list-style: none; margin: 0; padding: 0; }
.xp-head { display: flex; align-items: baseline; gap: 12px; }
.xp-rank { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 12px; color: var(--ch-ink3); min-width: 20px; font-variant-numeric: tabular-nums; }
.xp-name { font-size: 19px; line-height: 1.3; flex: 1; }
.xp-name a { color: inherit; text-decoration: none; border-bottom: 1px solid var(--ch-rule); }
.xp-name a:hover { border-bottom-color: var(--ch-ink2); }
.xp-score { font-size: 12px; color: var(--ch-ink3); font-variant-numeric: tabular-nums; }
.xp-card .ch-byline { margin: 2px 0 0 32px; }
.xp-papers { list-style: none; margin: 10px 0 0 32px; padding: 0; }
.xp-paper { padding: 10px 0 0; }
.xp-paper .ch-title { font-size: 14.5px; }
.xp-paper .ch-meta { margin-top: 4px; row-gap: 2px; }
.xp-quote { margin: 6px 0 0; font-size: 14px; }
.xp-counts { font-size: 13px; color: var(--ch-ink2); }
.xp-found { margin-top: 26px; }
.xp-found li { margin: 8px 0 0; font-size: 14px; }
.xp-found .ch-meta { margin-top: 2px; }
.xp-topic { margin: 14px 0 0; font-size: 13.5px; line-height: 1.5; color: var(--ch-ink); }
.xp-formula { font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.xp-changed { margin: 6px 0 0; font-size: 13px; font-style: italic; color: var(--ch-ink2); }
/* The search bar's options and button on one line at the page's width. */
.xp-form .ch-bar { align-items: center; gap: 16px; }
.xp-form .ch-bar-left { align-items: center; gap: 4px 14px; }
/* The checkboxes wrap together, onto their own row when the selects take the first. */
.xp-checks { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; }
.xp-form .ch-opts, .xp-form .ch-toggle { font-size: 11.5px; }
.xp-form .ch-go { font-size: 11px; letter-spacing: 0.1em; padding: 7px 14px; white-space: nowrap; flex-shrink: 0; }
.xp-live { margin: 8px 0 0; max-width: 620px; color: var(--ch-ink); }
.xp-live p { margin: 2px 0 0; }
.xp-live .xp-formula { padding-left: 18px; }
.xp-live .xp-note { color: var(--ch-ink3); }
.ch-faq li.xp-off { color: var(--ch-ink3); }
/* Each formula term and its definition share a muted tint. */
.xp-term { padding: 0 3px; border-radius: 3px; color: var(--ch-ink); white-space: nowrap; }
.xp-off .xp-term { opacity: 0.6; }
.xp-t-weight { background: #e7e9ed; }
.xp-t-share { background: #f3e2e1; }
.xp-t-found { background: #dcecea; }
.xp-t-topic { background: #e0e9f3; }
.xp-t-review { background: #f1e8d6; }
.xp-t-cites { background: #e1ede0; }
.xp-t-age { background: #ebe4f1; }
.xp-opt {margin: 0 6px; font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ch-ink3); border: 1px solid var(--ch-rule2); padding: 0 4px; }
@media (max-width: 560px) { .xp-card .ch-byline, .xp-papers { margin-left: 0; } }
/* On a phone the button goes below the options, so the checkboxes keep one row. */
@media (max-width: 560px) { .xp-form .ch-bar { flex-wrap: wrap; gap: 10px; } .xp-form .ch-bar-left { flex: 1 1 100%; } .xp-form .ch-go { margin-left: auto; } }
`;

const seconds = (ms: number | undefined): string => `${((ms ?? 0) / 1000).toFixed(1)}s`;

/** "Done at: search 0.7s · citations 0.8s · …" for the time's tooltip. The stages overlap. */
const timingTitle = (t: Record<string, number>): string =>
  t.reused != null
    ? "Reused the papers read for this topic in the last 6 hours; ranked again with the boxes as checked"
    : "Done at: " +
  [
    ["search", t.search],
    ["citations", t.citations],
    ["OpenAlex", t.openalex],
    ["read", t.judge],
    ["foundational", t.foundational],
  ]
    .filter(([, ms]) => ms != null)
    .map(([name, ms]) => `${name} ${seconds(ms as number)}`)
    .join(" · ");

const parsePapers = (raw: string | null): number => {
  const n = Number(raw);
  return PAPER_STEPS.includes(n) ? n : DEFAULT_PAPERS;
};

const parseRankBy = (raw: string | null): RankBy =>
  raw && raw in RANK_BY_LABELS ? (raw as RankBy) : "score";

export const ExpertsPage = () => {
  const { user, loading } = useAuth();
  const staff = isStaff(user?.email);
  const [params, setParams] = useSearchParams();
  // The query lives in the URL (?q=…&n=…&f=0&first=1&recent=1&rank=…), so a link reruns it.
  const q = (params.get("q") ?? "").trim();
  const n = parsePapers(params.get("n"));
  const f = params.get("f") !== "0";
  const fa = params.get("first") === "1";
  const rw = params.get("recent") === "1";
  const rb = parseRankBy(params.get("rank"));
  const [topic, setTopic] = useState(q);
  const [numPapers, setNumPapers] = useState(n);
  const [foundational, setFoundational] = useState(f);
  const [firstAuthors, setFirstAuthors] = useState(fa);
  const [recent, setRecent] = useState(rw);
  const [rankBy, setRankBy] = useState<RankBy>(rb);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExpertsSearchResponse | null>(null);
  const [faqOpen, setFaqOpen] = useState(false);
  /** Bumped to rerun the query already in the URL. */
  const [runs, setRuns] = useState(0);
  const inflight = useRef<AbortController | null>(null);
  const topicRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = topicRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [topic]);

  useEffect(() => () => inflight.current?.abort(), []);

  // Run whenever the URL's query changes: on load, on submit, on back and forward.
  useEffect(() => {
    if (!staff || !q) return;
    setTopic(q);
    setNumPapers(n);
    setFoundational(f);
    setFirstAuthors(fa);
    setRecent(rw);
    setRankBy(rb);
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setBusy(true);
    setError(null);
    setData(null);
    searchExperts(
      {
        topic: q,
        num_papers: n,
        foundational: f,
        first_authors: fa,
        recent: rw,
        rank_by: rb,
        limit: PEOPLE,
      },
      ctrl.signal,
    )
      .then((result) => {
        if (!ctrl.signal.aborted) setData(result);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Couldn't reach the experts search.");
      })
      .finally(() => {
        if (inflight.current === ctrl) setBusy(false);
      });
  }, [staff, q, n, f, fa, rw, rb, runs]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = topic.trim();
    if (!text || busy) return;
    const next = new URLSearchParams({ q: text });
    if (numPapers !== DEFAULT_PAPERS) next.set("n", String(numPapers));
    if (!foundational) next.set("f", "0");
    if (firstAuthors) next.set("first", "1");
    if (recent) next.set("recent", "1");
    if (rankBy !== "score") next.set("rank", rankBy);
    if (next.toString() === params.toString()) setRuns((r) => r + 1);
    else setParams(next);
  };

  const forbidden = !loading && !staff;
  const c = data?.counts;
  // The FAQ's formula follows the options as checked; a note says when they differ from
  // the options the results shown were ranked with.
  const options = { foundational, first_authors: firstAuthors, recent, rank_by: rankBy };
  const counting = rankBy !== "score";
  const changed = data ? changedOptions(data, options) : [];

  return (
    <div className="ch-root">
      <style>{PAGE_CSS + EXPERTS_CSS}</style>
      <div className="ch-wrap">
        <header>
          <h1 className="ch-kicker">GXL Paperclip · Experts · Staff preview</h1>
          <p className="ch-lede">Find the people whose work on a topic carries the most weight.</p>
          <p className="ch-source">
            Reads up to 1,000 search results and the landmark papers they cite, then ranks their
            authors by how much of their well-cited work is about the topic.
          </p>
        </header>
        {forbidden ? (
          <div className="ch-status" role="alert">
            403 · This page is for GXL staff only.
          </div>
        ) : (
          <>
            <form className="ch-form xp-form" onSubmit={submit}>
              <textarea
                ref={topicRef}
                className="ch-claim"
                rows={1}
                value={topic}
                maxLength={300}
                aria-label="Topic"
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }}
                placeholder="Enter a topic — e.g. KRAS G12C inhibitors, silicon photonics neural networks"
              />
              <div className="ch-bar">
                <div className="ch-bar-left">
                  <span className="ch-opts">
                    <label>
                      papers to read{" "}
                      <select
                        value={numPapers}
                        onChange={(e) => setNumPapers(Number(e.target.value))}
                      >
                        {PAPER_STEPS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  </span>
                  <span
                    className="ch-opts"
                    title="GXL score weighs each paper by topic fit, citations and author position. The others are plain counts over the on-topic papers"
                  >
                    <label>
                      rank by{" "}
                      <select value={rankBy} onChange={(e) => setRankBy(e.target.value as RankBy)}>
                        {(Object.keys(RANK_BY_LABELS) as RankBy[]).map((k) => (
                          <option key={k} value={k}>
                            {RANK_BY_LABELS[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </span>
                  <span className="xp-checks">
                    <label className="ch-toggle">
                      <input
                        type="checkbox"
                        checked={foundational}
                        onChange={(e) => setFoundational(e.target.checked)}
                      />
                      Foundational papers
                    </label>
                    <label
                      className="ch-toggle"
                      title={
                        counting
                          ? "Count only the papers they are first or sole author of"
                          : "Only first and sole authors, usually the people who did the work, earn credit"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={firstAuthors}
                        onChange={(e) => setFirstAuthors(e.target.checked)}
                      />
                      First authors only
                    </label>
                    <label
                      className="ch-toggle"
                      title={
                        counting
                          ? "Count only the papers from the last 3 years"
                          : "A paper's weight halves every 3 years: who leads the topic now. With first authors only: emerging researchers"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={recent}
                        onChange={(e) => setRecent(e.target.checked)}
                      />
                      Recent work
                    </label>
                  </span>
                </div>
                <button className="ch-go" type="submit" disabled={busy || !staff}>
                  Find experts
                </button>
              </div>
            </form>
            <div aria-live="polite">
              {busy ? (
                <div className="ch-status">
                  Reading up to {n.toLocaleString("en-US")} papers and the papers they cite most…
                </div>
              ) : null}
            </div>
            {error ? (
              <div className="ch-status" role="alert">
                {error}
              </div>
            ) : null}
            {data && c ? (
              <div className="ch-results">
                <div className="ch-controls">
                  <div className="xp-counts">
                    {data.experts.length} people · read {c.candidates.toLocaleString("en-US")}{" "}
                    papers ({c.relevant.toLocaleString("en-US")} on topic, {c.reviews} reviews) ·{" "}
                    {c.foundational} foundational
                    {isEmerging(data) ? " · emerging researchers" : null}
                    {data.rank_by && data.rank_by !== "score"
                      ? ` · ranked by ${RANK_BY_LABELS[data.rank_by].toLowerCase()}`
                      : null}
                  </div>
                  <div className="ch-right">
                    {data.experts.length ? (
                      <CopyButton
                        label="Copy as markdown"
                        title="Copy the people, their papers and the quotes as markdown"
                        build={() => expertsMarkdown(data)}
                      />
                    ) : null}
                    <span className="ch-time" title={timingTitle(data.timing_ms)}>
                      {seconds(data.timing_ms.total)}
                    </span>
                  </div>
                </div>
                {data.topic_guide ? (
                  <p className="xp-topic">
                    <strong>Topic read as:</strong> {data.topic_guide.description}
                    {data.topic_guide.excludes
                      ? ` Not counted: ${data.topic_guide.excludes.replace(/\.$/, "")}.`
                      : null}
                  </p>
                ) : null}
                {changed.length ? (
                  <p className="xp-changed">
                    Checked since this search: {changed.join(", ")}. Find experts to re-rank with
                    them.
                  </p>
                ) : null}
                {data.warnings?.map((w) => (
                  <div key={w} className="ch-status" role="status">
                    {w}
                  </div>
                ))}
                {data.experts.length ? (
                  <ol className="xp-list">
                    {data.experts.map((e, i) => (
                      <ExpertCard
                        key={`${e.name}-${i}`}
                        expert={e}
                        rank={i + 1}
                        recentSince={data.recent_since}
                        rankBy={data.rank_by ?? "score"}
                      />
                    ))}
                  </ol>
                ) : (
                  <div className="ch-none">No paper had this topic as a main subject.</div>
                )}
                {data.foundational_papers.length ? (
                  <details className="ch-details xp-found">
                    <summary>Foundational papers ({data.foundational_papers.length})</summary>
                    <ul>
                      {data.foundational_papers.map((p) => {
                        const url = paperUrl(p);
                        return (
                          <li key={p.doi ?? p.title}>
                            {url ? (
                              <a href={url} target="_blank" rel="noreferrer">
                                {p.title}
                              </a>
                            ) : (
                              p.title
                            )}
                            <div className="ch-meta">
                              {p.year ? <span>{p.year}</span> : null}
                              <span>{citedByText(p.cited_by_candidates ?? 0)}</span>
                              {p.citations != null ? (
                                <span title={CITATIONS_SOURCE}>
                                  {p.citations.toLocaleString("en-US")} citations
                                </span>
                              ) : null}
                              {(p.relevance ?? 0) < (data.min_relevance ?? 0.5) ? (
                                <span>not on topic</span>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}
            <div className="ch-about">
              <div className="ch-about-links">
                <button
                  type="button"
                  aria-expanded={faqOpen}
                  aria-controls="xp-faq"
                  onClick={() => setFaqOpen(!faqOpen)}
                >
                  How are people ranked?
                </button>
              </div>
              {faqOpen ? <ExpertsFaq options={options} /> : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
